import { computed, Injectable, signal } from '@angular/core';

/**
 * The scope a rule belongs to. The New Rule screen reads this so whatever the
 * list is filtered by arrives pre-selected.
 */
export interface DsmLocationRulesScope {
  projectId: string | null;
  modelId: string | null;
  elevationId: string | null;
  // triggerProductId: string | null; // Floor Plan filter removed from the list.
}

/**
 * Filter state for the Location Rules list, held outside the component so it
 * survives navigation to the create screen and back.
 *
 * The three ids cascade: a project owns models and a model owns elevations.
 * Changing one clears everything below it, because those ids no longer
 * belong to the new parent.
 *
 * A project is required — the list auto-selects the first one available — while
 * the other two are optional narrowing.
 */
@Injectable({ providedIn: 'root' })
export class DsmLocationRulesFiltersService {
  readonly projectId = signal<string | null>(null);
  readonly modelId = signal<string | null>(null);
  readonly elevationId = signal<string | null>(null);
  // Floor Plan filter removed from the list — the New/Edit Rule form has its own trigger select.
  // readonly triggerProductId = signal<string | null>(null);
  readonly searchTerm = signal('');

  /**
   * Bumped when a rule is created or changed so the list refetches. Kept here
   * rather than in the page because the form lives on a child route.
   */
  readonly rulesVersion = signal(0);

  readonly scope = computed<DsmLocationRulesScope>(() => ({
    projectId: this.projectId(),
    modelId: this.modelId(),
    elevationId: this.elevationId()
    // triggerProductId: this.triggerProductId(),
  }));

  /** Project is excluded: it is required, so it is scope rather than a filter. */
  readonly hasActiveFilters = computed(
    () => !!this.modelId() || !!this.elevationId() || /* !!this.triggerProductId() || */ !!this.searchTerm().trim()
  );

  setProject(projectId: string | null): void {
    this.projectId.set(projectId);
    this.modelId.set(null);
    this.elevationId.set(null);
    // this.triggerProductId.set(null);
  }

  setModel(modelId: string | null): void {
    this.modelId.set(modelId);
    this.elevationId.set(null);
    // this.triggerProductId.set(null);
  }

  setElevation(elevationId: string | null): void {
    this.elevationId.set(elevationId);
    // this.triggerProductId.set(null);
  }

  // Floor Plan filter removed from the list.
  // setTriggerProduct(triggerProductId: string | null): void {
  //   this.triggerProductId.set(triggerProductId);
  // }

  /** Restores a whole scope at once — used when the page opens on a deep link. */
  setScope(scope: Partial<DsmLocationRulesScope>): void {
    this.projectId.set(scope.projectId ?? null);
    this.modelId.set(scope.modelId ?? null);
    this.elevationId.set(scope.elevationId ?? null);
    // this.triggerProductId.set(scope.triggerProductId ?? null);
  }

  markRulesChanged(): void {
    this.rulesVersion.update(version => version + 1);
  }

  /** Clears the optional filters; the project stays, since one is always required. */
  reset(): void {
    this.setModel(null);
    this.searchTerm.set('');
  }
}
